import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GlobalState } from '../../types/store';

const initialState: GlobalState = {
  loading: false,
  error: null,
  user: null,
  isAuthenticated: false,
  isAuthorized: false,
  currentRoute: '/',
  backendVersion: null,
};

/**
 * Global state slice for application-wide state management
 * Handles authentication, authorization, loading, and routing state
 */
const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    /**
     * Sets global loading state
     * @param {GlobalState} state - Current state
     * @param {PayloadAction<boolean>} action - Loading state
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    /**
     * Sets global error message
     * @param {GlobalState} state - Current state
     * @param {PayloadAction<string | null>} action - Error message
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    /**
     * Sets current user
     * @param {GlobalState} state - Current state
     * @param {PayloadAction<any>} action - User object
     */
    setUser: (state, action: PayloadAction<any>) => {
      state.user = action.payload;
    },
    /**
     * Sets authentication status
     * @param {GlobalState} state - Current state
     * @param {PayloadAction<boolean>} action - Authentication status
     */
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    /**
     * Sets authorization status
     * @param {GlobalState} state - Current state
     * @param {PayloadAction<boolean>} action - Authorization status
     */
    setAuthorized: (state, action: PayloadAction<boolean>) => {
      state.isAuthorized = action.payload;
    },
    /**
     * Sets current route
     * @param {GlobalState} state - Current state
     * @param {PayloadAction<string>} action - Route path
     */
    setCurrentRoute: (state, action: PayloadAction<string>) => {
      state.currentRoute = action.payload;
    },
    setBackendVersion: (state, action: PayloadAction<string | null>) => {
      state.backendVersion = action.payload;
    },
  },
});

export const { setLoading, setError, setUser, setAuthenticated, setAuthorized, setCurrentRoute, setBackendVersion } = globalSlice.actions;
export default globalSlice.reducer;
